import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getDoctors, getQueueToday, type QueueTodayResponse } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Loader2 } from 'lucide-react';
import { usePageTitle } from "@/hooks/usePageTitle";

const REFRESH_SECS = 25;
const LS_KEY = 'frontdesk_token_display_doctorId';

function drName(name: string) {
  return name.startsWith('Dr.') ? name : `Dr. ${name}`;
}

// ─── Setup screen ─────────────────────────────────────────────────────────────

function SetupScreen({
  doctors,
  loading,
  onStart,
}: {
  doctors: { id: string; name: string }[];
  loading: boolean;
  onStart: (doctorId: string) => void;
}) {
  const [selected, setSelected] = useState('');

  useEffect(() => {
    if (doctors.length === 1) setSelected(doctors[0].id);
  }, [doctors]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-8">
      <div className="w-full max-w-sm text-center">
        <div className="mb-8">
          <div className="w-16 h-16 bg-primary rounded-2xl flex items-center justify-center mx-auto mb-4">
            <span className="text-2xl font-black text-primary-foreground">#</span>
          </div>
          <h1 className="text-2xl font-bold text-foreground">Token Display</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Set up the waiting area screen
          </p>
        </div>

        {loading ? (
          <Loader2 className="w-6 h-6 animate-spin text-primary mx-auto" />
        ) : doctors.length === 0 ? (
          <p className="text-sm text-muted-foreground">No doctors found.</p>
        ) : (
          <>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">
              Select doctor
            </p>
            <div className="space-y-2 mb-8">
              {doctors.map(d => (
                <button
                  key={d.id}
                  onClick={() => setSelected(d.id)}
                  className={`w-full px-4 py-3 rounded-xl text-sm font-medium border-2 transition-all ${
                    selected === d.id
                      ? 'border-primary bg-primary/5 text-primary'
                      : 'border-border bg-card text-foreground hover:border-primary/40'
                  }`}
                >
                  {drName(d.name)}
                </button>
              ))}
            </div>
            <button
              disabled={!selected}
              onClick={() => onStart(selected)}
              className="w-full py-3 rounded-xl text-sm font-bold bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Start Display →
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Display screen ───────────────────────────────────────────────────────────

function DisplayScreen({
  doctorName,
  queue,
  countdown,
  onReset,
}: {
  doctorName: string;
  queue: QueueTodayResponse | null;
  countdown: number;
  onReset: () => void;
}) {
  const inConsult = queue?.entries.find(e => e.status === 'in_consultation');
  const waiting   = queue?.entries.filter(e => e.status === 'waiting').sort((a, b) => a.sortOrder - b.sortOrder) ?? [];

  return (
    <div className="min-h-screen bg-background flex flex-col select-none">

      {/* Top bar */}
      <div className="flex items-center justify-between px-8 py-4 border-b border-border">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
            <span className="text-sm font-black text-primary-foreground">#</span>
          </div>
          <span className="font-bold text-foreground">OneCheckUp</span>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-sm text-muted-foreground">{drName(doctorName)}</span>
          {/* Refresh countdown bar */}
          <div className="flex items-center gap-2">
            <div className="w-20 h-1.5 bg-muted rounded-full overflow-hidden">
              <div
                className="h-full bg-primary rounded-full transition-all duration-1000"
                style={{ width: `${(countdown / REFRESH_SECS) * 100}%` }}
              />
            </div>
            <span className="text-xs text-muted-foreground tabular-nums">{countdown}s</span>
          </div>
          <button
            onClick={onReset}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors px-2 py-1 rounded-lg hover:bg-muted"
          >
            Change
          </button>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col items-center justify-center px-8 py-12 gap-12">

        {/* Now serving */}
        <div className="text-center">
          <p className="text-xs font-bold tracking-widest uppercase text-muted-foreground mb-4">
            Now Serving
          </p>
          {inConsult ? (
            <div className="flex flex-col items-center gap-4">
              <div className="w-48 h-48 rounded-full border-4 border-primary bg-primary/5 flex items-center justify-center">
                <span className="text-8xl font-black text-primary tabular-nums">
                  {inConsult.tokenNumber}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />
                <span className="text-sm font-medium text-muted-foreground">In consultation</span>
              </div>
            </div>
          ) : (
            <div className="w-48 h-48 rounded-full border-4 border-dashed border-border bg-muted/10 flex items-center justify-center">
              <span className="text-muted-foreground text-sm">No active<br/>consultation</span>
            </div>
          )}
        </div>

        {/* Waiting queue */}
        {waiting.length > 0 && (
          <div className="text-center">
            <p className="text-xs font-bold tracking-widest uppercase text-muted-foreground mb-5">
              Waiting · {waiting.length}
            </p>
            <div className="flex items-center justify-center gap-4 flex-wrap">
              {waiting.slice(0, 8).map((entry, i) => (
                <div
                  key={entry.id}
                  className={`flex items-center justify-center rounded-2xl font-bold tabular-nums transition-all ${
                    i === 0
                      ? 'w-20 h-20 text-3xl bg-warning/10 text-warning border-2 border-warning/30'
                      : 'w-14 h-14 text-xl bg-muted/30 text-muted-foreground border border-border'
                  }`}
                >
                  {entry.tokenNumber}
                </div>
              ))}
              {waiting.length > 8 && (
                <div className="w-14 h-14 rounded-2xl bg-muted/20 border border-border flex items-center justify-center text-sm text-muted-foreground">
                  +{waiting.length - 8}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Empty state */}
        {!inConsult && waiting.length === 0 && (
          <p className="text-muted-foreground text-lg">Queue is empty</p>
        )}
      </div>

      {/* Bottom: date + time */}
      <LiveClock />
    </div>
  );
}

function LiveClock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="px-8 py-4 border-t border-border text-center">
      <p className="text-sm text-muted-foreground">
        {now.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
        {' · '}
        {now.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true })}
      </p>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function TokenDisplayPage() {
  usePageTitle('Token display');
  const { currentUser } = useAuth();
  const clinicId = currentUser?.clinic_id ?? '';

  const [doctors, setDoctors]     = useState<{ id: string; name: string }[]>([]);
  const [doctorId, setDoctorId]   = useState<string | null>(() => localStorage.getItem(LS_KEY));
  const [queue, setQueue]         = useState<QueueTodayResponse | null>(null);
  const [loading, setLoading]     = useState(true);
  const [countdown, setCountdown] = useState(REFRESH_SECS);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Load doctors once
  useEffect(() => {
    getDoctors()
      .then(d => setDoctors(Array.isArray(d) ? d : []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Fetch queue for selected doctor
  const fetchQueue = useCallback(async () => {
    if (!doctorId || !clinicId) return;
    try {
      const data = await getQueueToday(doctorId, clinicId);
      if (data) setQueue(data);
    } catch {}
    setCountdown(REFRESH_SECS);
  }, [doctorId, clinicId]);

  // Auto-refresh polling
  useEffect(() => {
    if (!doctorId) return;
    fetchQueue();
    timerRef.current = setInterval(fetchQueue, REFRESH_SECS * 1000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [doctorId, fetchQueue]);

  // Countdown tick
  useEffect(() => {
    if (!doctorId) return;
    const t = setInterval(() => setCountdown(prev => (prev > 0 ? prev - 1 : REFRESH_SECS)), 1000);
    return () => clearInterval(t);
  }, [doctorId]);

  const handleStart = (id: string) => {
    localStorage.setItem(LS_KEY, id);
    setDoctorId(id);
    setCountdown(REFRESH_SECS);
  };

  const handleReset = () => {
    localStorage.removeItem(LS_KEY);
    setDoctorId(null);
    setQueue(null);
  };

  const activeDoctorName = doctors.find(d => d.id === doctorId)?.name ?? '';

  if (!doctorId) {
    return <SetupScreen doctors={doctors} loading={loading} onStart={handleStart} />;
  }

  return (
    <DisplayScreen
      doctorName={activeDoctorName}
      queue={queue}
      countdown={countdown}
      onReset={handleReset}
    />
  );
}
